'use client'
import { useState, useEffect } from 'react'
import { useRouter } from 'next/navigation'
import { useAuth } from '@/contexts/AuthContext'
import { useError } from '@/contexts/ErrorContext'
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Form, FormField, FormItem, FormControl, FormMessage } from "@/components/ui/form"
import { toast } from "sonner"
import { zodResolver } from "@hookform/resolvers/zod"
import * as z from "zod"
import { useDocumentTitle } from '@/hooks/useDocumentTitle'
import Link from 'next/link'
import { User, Lock } from 'lucide-react'
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card"
import { useForm } from "react-hook-form"

const formSchema = z.object({
  username: z.string().min(1, "ユーザー名を入力してください。"),
  password: z.string().min(1, "パスワードを入力してください。"),
})

export default function Login() {
  useDocumentTitle('ログイン')
  const [isLoading, setIsLoading] = useState(false)
  const router = useRouter()
  const { login, isLoggedIn } = useAuth()
  const { handleError } = useError()

  useEffect(() => {
    if (isLoggedIn) {
      const params = new URLSearchParams(window.location.search);
      const redirectPath = params.get('redirect') ?? '/user/dashboard';
      router.push(redirectPath);
    }
  }, [isLoggedIn, router]);

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      username: "",
      password: "",
    },
  })

  const onSubmit = async (values: z.infer<typeof formSchema>) => {
    setIsLoading(true);
    try {
      await login(values.username, values.password);
      router.push('/user/dashboard');
      toast.success("ログインしました");
    } catch {
      handleError('LOGIN_ERROR');
    } finally {
      setIsLoading(false);
    }
  };

  return (
      <div className="flex min-h-[calc(100vh-150px)] flex-col items-center justify-center p-4 sm:p-24">
        <Card className="w-full max-w-md mx-4 shadow-lg transition-shadow duration-300 hover:shadow-xl">
          <CardHeader className="space-y-1">
            <CardTitle className="text-2xl text-center">ログイン</CardTitle>
          </CardHeader>
          <CardContent>
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4" suppressHydrationWarning>
                <FormField
                    control={form.control}
                    name="username"
                    render={({ field }) => (
                        <FormItem>
                          <FormControl>
                            <div className="relative">
                              <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500" size={18} />
                              <Input
                                  autoComplete="username"
                                  placeholder="ユーザー名"
                                  className="pl-10"
                                  disabled={isLoading}
                                  {...field}
                              />
                            </div>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                    )}
                />
                <FormField
                    control={form.control}
                    name="password"
                    render={({ field }) => (
                        <FormItem>
                          <FormControl>
                            <div className="relative">
                              <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500" size={18} />
                              <Input
                                  type="password"
                                  autoComplete="current-password"
                                  placeholder="パスワード"
                                  className="pl-10"
                                  disabled={isLoading}
                                  {...field}
                              />
                            </div>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                    )}
                />
                <Button type="submit" className="w-full" disabled={isLoading}>
                  {isLoading ? 'ログイン中...' : 'ログイン'}
                </Button>
              </form>
            </Form>
            <div className="mt-4 space-y-2 text-center text-sm">
              <p>
                <Link href="/register" className="text-blue-500 hover:underline">アカウント作成</Link>
              </p>
              <p>
                <Link href="/forgot-password" className="text-blue-500 hover:underline">パスワードをお忘れですか？</Link>
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
  )
}
